import { clearStream } from 'src/utils/media/stream';
import { detachMediaElement } from 'src/utils/media/view';

import { getImage } from './utils';

/**
 * Video와 Canvas input을 모두 지원한다.
 */
export type PipeInputEl = HTMLVideoElement | HTMLCanvasElement;

class MediaStreamPipe {
  protected inputEl: PipeInputEl | null = null;

  /**
   * 송출용 MediaStream (미디어 스트림 객체는 유지한 채 track만 변경하여 제어)
   */
  readonly mediaStream: MediaStream = new MediaStream();

  protected outputCanvasEls: HTMLCanvasElement[] = [];

  /** video면 videoWidth/videoHeight, canvas면 width/height. input이 없으면 null. */
  protected getInputSize(): { width: number; height: number } | null {
    const inputEl = this.inputEl;
    if (!inputEl) {
      return null;
    }

    if (inputEl instanceof HTMLVideoElement) {
      return { width: inputEl.videoWidth, height: inputEl.videoHeight };
    }
    return { width: inputEl.width, height: inputEl.height };
  }

  /** video면 HAVE_ENOUGH_DATA 여부, canvas면 크기가 0이 아닌지로 판단한다. */
  protected isInputReady(): boolean {
    const inputEl = this.inputEl;
    if (!inputEl) {
      return false;
    }
    if (inputEl instanceof HTMLVideoElement) {
      return inputEl.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA;
    }
    return inputEl.width > 0 && inputEl.height > 0;
  }

  protected drawOriginalVideo() {
    const inputEl = this.inputEl;
    if (!inputEl || this.outputCanvasEls.length === 0) {
      return;
    }

    const sourceImage = getImage(inputEl);
    if (!sourceImage) {
      return;
    }

    const size = this.getInputSize();
    if (!size) {
      return;
    }
    const { width, height } = size;

    // 모든 outputCanvasEls에 렌더링
    this.outputCanvasEls.forEach((outputCanvasEl) => {
      outputCanvasEl.width = width;
      outputCanvasEl.height = height;
      const context = outputCanvasEl.getContext('2d');
      if (!context) {
        return;
      }

      context.clearRect(0, 0, width, height);
      context.drawImage(sourceImage, 0, 0, width, height);
    });
  }

  public setEl(inputEl: PipeInputEl, outputCanvasEl?: HTMLCanvasElement | HTMLCanvasElement[]) {
    this.inputEl = inputEl;

    if (outputCanvasEl) {
      if (Array.isArray(outputCanvasEl)) {
        this.outputCanvasEls = outputCanvasEl;
      } else {
        this.outputCanvasEls = [outputCanvasEl];
      }
    } else {
      // 기본적으로 하나의 canvas 생성
      this.outputCanvasEls = [document.createElement('canvas')];
    }
  }

  public updateMediaStream() {
    // clear previous tracks
    clearStream(this.mediaStream);

    const firstCanvas = this.outputCanvasEls[0];
    if (firstCanvas) {
      /**
       * firefox에서 captureStream() 호출 시, NS_ERROR_NOT_INITIALIZED 에러 발생함
       * @see https://bugzilla.mozilla.org/show_bug.cgi?id=1572422
       */
      firstCanvas.getContext('2d');

      // 새로운 대표 canvas를 기반으로 track 대체
      this.mediaStream.addTrack(firstCanvas.captureStream().getVideoTracks()[0]);
    }
  }

  public stopRendering() {}

  public dispose() {
    if (this.inputEl instanceof HTMLVideoElement) {
      detachMediaElement(this.inputEl);
    }
    this.inputEl = null;

    clearStream(this.mediaStream);
    this.outputCanvasEls = [];
  }

  public addOutputCanvas(canvas: HTMLCanvasElement) {
    if (!this.outputCanvasEls.includes(canvas)) {
      this.outputCanvasEls.push(canvas);
      this.updateMediaStream();
    }
  }

  public removeOutputCanvas(canvas: HTMLCanvasElement) {
    const index = this.outputCanvasEls.indexOf(canvas);
    if (index > -1) {
      this.outputCanvasEls.splice(index, 1);
      this.updateMediaStream();
    }
  }

  public startRendering() {}

  public renderVideo() {}

  public render() {}

  public getInputEl() {
    return this.inputEl;
  }

  public getOutputCanvasEls(): HTMLCanvasElement[] {
    return this.outputCanvasEls;
  }
}

export default MediaStreamPipe;
